declare global {
    interface Window {
        gapi: any // eslint-disable-line @typescript-eslint/no-explicit-any
        google: any // eslint-disable-line @typescript-eslint/no-explicit-any
    }
}

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RiCloseLine, RiDriveLine, RiLoader4Line } from 'react-icons/ri'
import toast from 'react-hot-toast'
import { useGoogleDriveImport } from '@/api/useImport'
import { buildRoute } from '@/constants/routes'
import styles from './GoogleDriveImportModal.module.css'

interface GoogleDriveImportModalProps {
    visible: boolean
    onClose: () => void
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined
const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY as string | undefined

const SCOPE = 'https://www.googleapis.com/auth/drive.readonly'
const PICKER_MIME_TYPES = 'image/png,image/jpeg,image/webp,application/pdf'

function loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve()
            return
        }
        const script = document.createElement('script')
        script.src = src
        script.onload = () => resolve()
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`))
        document.head.appendChild(script)
    })
}

export function GoogleDriveImportModal({ visible, onClose }: GoogleDriveImportModalProps) {
    const navigate = useNavigate()
    const importMutation = useGoogleDriveImport()

    const [pickerLoaded, setPickerLoaded] = useState(false)
    const [loadingPicker, setLoadingPicker] = useState(false)
    const [importingFile, setImportingFile] = useState(false)
    const tokenClientRef = useRef<any>(null) // eslint-disable-line @typescript-eslint/no-explicit-any

    const missingApiKey = !GOOGLE_API_KEY
    const missingClientId = !GOOGLE_CLIENT_ID

    // Load gapi and picker when modal opens
    useEffect(() => {
        if (!visible || pickerLoaded || missingApiKey || missingClientId) return

        let cancelled = false
        setLoadingPicker(true)

        const init = async () => {
            try {
                await loadScript('https://apis.google.com/js/api.js')
                await new Promise<void>((resolve, reject) => {
                    window.gapi.load('picker', {
                        callback: resolve,
                        onerror: () => reject(new Error('gapi.load picker failed')),
                    })
                })
                if (!cancelled) setPickerLoaded(true)
            } catch (err) {
                console.error('Google Picker init error:', err)
                if (!cancelled) toast.error('Error al cargar Google Picker')
            } finally {
                if (!cancelled) setLoadingPicker(false)
            }
        }

        init()

        return () => { cancelled = true }
    }, [visible, pickerLoaded, missingApiKey, missingClientId])

    const openPicker = useCallback((accessToken: string) => {
        if (!window.google?.picker) return

        const picker = new window.google.picker.PickerBuilder()
            .setAppId(GOOGLE_CLIENT_ID!)
            .setOAuthToken(accessToken)
            .setDeveloperKey(GOOGLE_API_KEY!)
            .addView(
                new window.google.picker.DocsView()
                    .setMimeTypes(PICKER_MIME_TYPES)
                    .setIncludeFolders(false)
            )
            .setCallback(async (data: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
                if (data.action !== window.google.picker.Action.PICKED) return

                const doc = data.docs?.[0]
                if (!doc) return

                setImportingFile(true)
                try {
                    const project = await importMutation.mutateAsync({
                        google_access_token: accessToken,
                        file_id: doc.id as string,
                        file_name: doc.name as string,
                        mime_type: doc.mimeType as string,
                    })
                    toast.success(`"${project.name}" importado`)
                    onClose()
                    navigate(buildRoute.editor(project.id))
                } catch {
                    toast.error('Error al importar desde Google Drive')
                } finally {
                    setImportingFile(false)
                }
            })
            .build()

        picker.setVisible(true)
    }, [importMutation, onClose, navigate])

    const handleSelectFile = useCallback(async () => {
        if (!pickerLoaded || missingApiKey || missingClientId) return

        // Load Google Identity Services if needed
        try {
            await loadScript('https://accounts.google.com/gsi/client')
        } catch {
            toast.error('Error al cargar Google Identity Services')
            return
        }

        // Initialise or reuse token client
        if (!tokenClientRef.current) {
            tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
                client_id: GOOGLE_CLIENT_ID!,
                scope: SCOPE,
                callback: (response: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
                    if (response.error) {
                        toast.error('No se pudo obtener acceso a Google Drive')
                        return
                    }
                    openPicker(response.access_token as string)
                },
            })
        }

        tokenClientRef.current.requestAccessToken({ prompt: 'consent' })
    }, [pickerLoaded, missingApiKey, missingClientId, openPicker])

    if (!visible) return null

    const isBusy = loadingPicker || importingFile

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <div className={styles.headerIcon}>
                            <RiDriveLine />
                        </div>
                        <h2 className={styles.title}>Importar desde Google Drive</h2>
                    </div>
                    <button className={styles.closeBtn} onClick={onClose} disabled={isBusy}>
                        <RiCloseLine />
                    </button>
                </div>

                {/* Body */}
                <div className={styles.body}>
                    {missingApiKey ? (
                        <div className={styles.missingConfig}>
                            <RiDriveLine className={styles.missingIcon} />
                            <p className={styles.missingText}>
                                Configura <code>VITE_GOOGLE_API_KEY</code> para usar Google Drive
                            </p>
                        </div>
                    ) : missingClientId ? (
                        <div className={styles.missingConfig}>
                            <RiDriveLine className={styles.missingIcon} />
                            <p className={styles.missingText}>
                                Configura <code>VITE_GOOGLE_CLIENT_ID</code> para usar Google Drive
                            </p>
                        </div>
                    ) : importingFile ? (
                        <div className={styles.loadingState}>
                            <RiLoader4Line className={styles.spinner} />
                            <p>Importando archivo...</p>
                        </div>
                    ) : (
                        <div className={styles.content}>
                            <div className={styles.driveIllustration}>
                                <RiDriveLine className={styles.driveIcon} />
                            </div>
                            <p className={styles.description}>
                                Selecciona una imagen o PDF de tu Google Drive para importarlo como proyecto.
                            </p>
                            <button
                                className={styles.selectBtn}
                                onClick={handleSelectFile}
                                disabled={loadingPicker}
                            >
                                {loadingPicker ? (
                                    <>
                                        <RiLoader4Line className={styles.spinner} />
                                        Cargando...
                                    </>
                                ) : (
                                    <>
                                        <RiDriveLine />
                                        Seleccionar archivo
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
